// @ts-strict-ignore
import { MetaElementUsageIdentityVO, TextureAreaUsageVO } from '@shared/vo/meta-element.vo';
import { PropertyVO } from '../../article/model/vo/property.vo';
import { Connection } from '@3d-scene/model3d/connection';
import { Rectangle3D } from '@3d-scene/model/rectangle-3d';
import { Dictionary } from 'typescript-collections';
import { Line } from '@3d-scene/model/line';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';

export class LabelStripTexture {
  public usage: TextureAreaUsageVO;
  // accepting connection on backward article directly connected to label-strip, can be null in case of label-strip on adapter
  public directAcceptingId: MetaElementUsageIdentityVO;
  // reference to entire terminal placed beneath texture connection in the middle of the label-strip on adapters
  public referenceId: MetaElementUsageIdentityVO;
  public localRectangle: Rectangle3D;
  public distanceToPreviousConnection: number = 0;

  public get identity(): MetaElementUsageIdentityVO {
    return this.usage.identity;
  }

  public get backwardAcceptingId(): MetaElementUsageIdentityVO {
    return !!this.directAcceptingId ? this.directAcceptingId : this.referenceId;
  }
}

export class LabelStrip {
  public originalUsageId: string;
  // array is used here since original label-strip could be split into multiple articles
  public newUsageIds: string[] = [];
  public articleId: number;
  public articleVersion: number;
  // list of accepting connections which are connected directly with label-strip
  // used to connect re-created label-strip
  public backwardAcceptingIds: MetaElementUsageIdentityVO[] = [];
  public textures: LabelStripTexture[] = [];
  public featureProperties: PropertyVO[] = [];
  public onAdapter: boolean;
  public hasNotConnectedReferenceIds: boolean;
  public articleLine: Line;

  constructor(private connectionInfoService: ConnectionInfoService) {}

  public isConnectable(): boolean {
    // label-strip on adapters should not remain on single adapter
    const minAcceptingConnectionsCount: number = this.hasNotConnectedReferenceIds ? 2 : 1;
    let actualCount: number = 0;
    for (const backwardId of this.backwardAcceptingIds) {
      const acceptingConnection: Connection = this.connectionInfoService.findConnectionByIdentity(backwardId);
      if (acceptingConnection) {
        actualCount++;
        if (actualCount === minAcceptingConnectionsCount) {
          return true;
        }
      }
    }
    return false;
  }

  public getDirectAcceptingConnections(): Connection[] {
    const acceptingConnections: Connection[] = [];
    for (const backwardId of this.backwardAcceptingIds) {
      const acceptingConnection: Connection = this.connectionInfoService.findConnectionByIdentity(backwardId);
      if (acceptingConnection) {
        acceptingConnections.push(acceptingConnection);
      }
    }
    return acceptingConnections;
  }
}

export class LabelStripTextureStorage {
  private readonly _textures: LabelStripTexture[];
  private readonly _backwardIdToTexture: Dictionary<MetaElementUsageIdentityVO, LabelStripTexture> = new Dictionary<
    MetaElementUsageIdentityVO,
    LabelStripTexture
  >();
  private readonly _identityToTexture: Dictionary<MetaElementUsageIdentityVO, LabelStripTexture> = new Dictionary<
    MetaElementUsageIdentityVO,
    LabelStripTexture
  >();

  constructor(textures: LabelStripTexture[]) {
    this._textures = textures;
    textures.forEach((texture) => {
      this._backwardIdToTexture.setValue(texture.backwardAcceptingId, texture);
      this._identityToTexture.setValue(texture.identity, texture);
    });
  }

  public get textures(): LabelStripTexture[] {
    return this._textures;
  }

  public getTextureByBackwardId(backwardId: MetaElementUsageIdentityVO): LabelStripTexture {
    return this._backwardIdToTexture.getValue(backwardId);
  }

  public getTextureByIdentity(identity: MetaElementUsageIdentityVO): LabelStripTexture {
    return this._identityToTexture.getValue(identity);
  }
}
